import key from 'ally.js/esm/when/key';
import debounce from 'lodash/debounce';
import {checkVerticalresize, getHeight, triggerEvent} from '../base/utils';
import A11y from './a11y';
import FocusHandler from './focus-handler';
import Popup from './popup';

class Modal {
    constructor($link, hash, options) {
        let [id, anchor, polygon, ..._] = hash.split(',');
        this.id = id;
        this.anchor = anchor;
        this.polygon = polygon;
        this.$link = $link;
        this.$dialog = document.getElementById(id);
        this.options = options || {};

        if (!this.$link || !this.$dialog) {
            return;
        }

        this.$inner = this.$dialog.querySelector('.modal-inner');
        this.$overlay = this.$dialog.querySelector('.modal-overlay');
        this.$contents = this.$dialog.querySelector('.modal-popup');
        this.$titleWrap = this.$dialog.querySelector('.modal-title');
        this.$title = this.$titleWrap.querySelector('.modal-title-inner');

        this.init();
    }

    init() {
        this.handleResize();
        this.$link.setAttribute('aria-controls', `${this.id}-popup`);
        this.$link.setAttribute('aria-expanded', 'false');
        this.$link.setAttribute('aria-haspopup', 'dialog');
        if (!this.$link.title) {
            this.$link.title = 'Otwórz okno modalne';
        }

        this.events();

        this.a11yHandlers = null;
    }

    events() {
        this.$link.addEventListener('click', e => {
            e.preventDefault();

            this.showDialog();
            setTimeout(() => this.$dialog.focus(), 200);
        });

        for (let $el of this.$dialog.querySelectorAll('.popup-close')) {
            $el.addEventListener('click', e => {
                if (this.$link.getAttribute('aria-expanded') === 'true') {
                    this.hideDialog(e.target);
                    setTimeout(() => this.$link.focus(), 200);
                }
            });
        }

        document.body.addEventListener('modal-close', this.hideDialog.bind(this));
        this.$overlay.addEventListener('click', this.handleOverlayClick.bind(this));
        this.$overlay.addEventListener('wheel', this.handleOverlayScroll.bind(this));
        window.addEventListener('resize', debounce(this.handleResize.bind(this), 100));
        window.addEventListener('load', this.resizeTitle.bind(this));
        this.$inner.addEventListener('scroll', debounce(this.fixTitle.bind(this), 50, {trailing: true}));
    }

    handleResize() {
        checkVerticalresize(this.windowWidth, windowWidth => {
            this.windowWidth = windowWidth;
            this.resizeTitle();
            this.scrollBarWidth = this.$inner.offsetWidth - this.$inner.clientWidth + 'px';
            this.$overlay.style.right = this.scrollBarWidth;
        });
    }

    resizeTitle() {
        if (this.windowWidth === document.documentElement.clientWidth){
            return;
        }

        let titleHeight,
            titleWrapHeight;

        titleWrapHeight = getHeight(this.$titleWrap, 'full', 200, () => {
            this.$title.style.maxHeight = 'none';
            titleHeight = this.$title.offsetHeight;
            this.$title.style.maxHeight = titleHeight + 'px';
        });

        this.$contents.style.paddingTop = titleWrapHeight + 'px';
    }

    fixTitle() {
        let top = this.$inner.scrollTop === 0;

        if (top) {
            clearTimeout(this.timeout);
            this.$titleWrap.classList.add('on-top');
            this.$titleWrap.classList.remove('not-on-top');
            this.$titleWrap.classList.remove('nowrap');
        } else {
            clearTimeout(this.timeout);
            this.$titleWrap.classList.add('not-on-top');
            this.$titleWrap.classList.remove('on-top');
            this.timeout = setTimeout(() => {
                this.$titleWrap.classList.add('nowrap');
            }, 400);
        }
    }

    showDialog() {
        if (this.options.onOpen && typeof this.options.onOpen === 'function') {
            this.options.onOpen();
        }

        Popup.open(this.$dialog);
        this.setScrollbar();
        this.$link.setAttribute('aria-expanded', 'true');
        this.$contents.setAttribute('aria-hidden', 'false');
        this.a11yHandlers = A11y.focusTrap(this.$dialog, this.$link, this.hideDialog.bind(this));

        setTimeout(() => {
            if (this.anchor) {
                const $anchor = document.getElementById(this.anchor);

                FocusHandler.focus($anchor);

                if (this.polygon) {
                    const $polygon = document.querySelector(`polygon[data-id=${this.polygon}]`),
                          $centreMap = $anchor.querySelector('.centre-plan');

                    if ($polygon) {
                        $polygon.classList.add('selected');

                        if ($centreMap) {
                            this.$inner.scroll({
                                top: $polygon.getBoundingClientRect().top - (window.innerHeight / 2),
                                left: 0,
                                behavior: 'smooth'
                            });

                            $centreMap.scroll({
                                top: 0,
                                left: $polygon.getBoundingClientRect().left - (window.innerWidth / 2),
                                behavior: 'smooth'
                            });
                        }
                    }
                }
            } else {
                this.$contents.focus();
            }
        }, 200);
    }

    hideDialog($trigger) {
        if (typeof this.options.onClose === 'function') {
            this.options.onClose($trigger);
        }

        Popup.close(this.$dialog);
        this.unsetScrollbar();
        this.$link.setAttribute('aria-expanded', 'false');
        this.$contents.setAttribute('aria-hidden', 'true');
        this.a11yHandlers = A11y.removeFocusTrap(this.a11yHandlers);

        if (this.polygon) {
            const $polygon = document.querySelector(`polygon[data-id=${this.polygon}]`);

            if ($polygon) {
                $polygon.classList.remove('selected');
            }
        }

        setTimeout(() => {
            if (this.anchor) {
                const $centreMaps = this.$dialog.querySelectorAll('.centre-plan');

                if ($centreMaps.length) {
                    for (let map of $centreMaps) {
                        map.scroll({top: 0, left: 0});
                    }
                }
            }

            this.$inner.scroll({top: 0, left: 0});
            triggerEvent(document.body, 'modal-closed', this.$dialog);
        }, 200);
    }

    setScrollbar() {
        let $header = document.querySelector('.header'),
            $cookies = document.querySelector('#cookies');

        document.body.style.paddingRight = this.scrollBarWidth;
        if ($header) $header.style.paddingRight = this.scrollBarWidth;
        if ($cookies) $cookies.style.marginRight = this.scrollBarWidth;
    }

    unsetScrollbar() {
        let $header = document.querySelector('.header'),
            $cookies = document.querySelector('#cookies');

        document.body.style.paddingRight = '';
        if ($header) $header.style.paddingRight = '';
        if ($cookies) $cookies.style.marginRight = '';
    }

    handleOverlayScroll(e) {
        switch (e.deltaMode) {
            case 0: 		//DOM_DELTA_PIXEL		Chrome
                this.$inner.scrollTop += e.deltaY;
                this.$inner.scrollLeft += e.deltaX;
                break;
            case 1: 		//DOM_DELTA_LINE		Firefox
                this.$inner.scrollTop += 15 * e.deltaY;
                this.$inner.scrollLeft += 15 * e.deltaX;
                break;
            case 2: 		//DOM_DELTA_PAGE
                this.$inner.scrollTop += 0.03 * e.deltaY;
                this.$inner.scrollLeft += 0.03 * e.deltaX;
                break;
        }
        e.stopPropagation();
        e.preventDefault();
    }

    handleOverlayClick() {
        this.hideDialog();
    }
}

export default Modal;
